/**
 * Mermaid fence rendering for agent prose and export previews (WS-H,
 * chat-round 2026-08-25 item 6).
 *
 *  - LAZY: the mermaid bundle loads only when a ```mermaid fence actually
 *    renders (dynamic import — never in the shell or chat chunk).
 *  - TOKEN-THEMED: mermaid's `base` theme with themeVariables read from the
 *    live design tokens at render time, so diagrams match the app in BOTH
 *    themes with nothing hard-coded. The theme is an INPUT to the render:
 *    mermaid bakes the colours into the SVG it returns, so a diagram left on
 *    screen through a theme toggle has to be drawn again or it keeps the
 *    colours of the theme it was born in.
 *  - ERROR-SAFE: a diagram that fails to load, parse or render (including a
 *    fence still streaming in) falls back to the plain code block — never a
 *    crash, never a blank hole. Chips/citations around the fence are
 *    untouched (this component owns only the fence body).
 *  - NO FLICKER: a re-render (theme toggle, a growing streamed fence) keeps
 *    the previous SVG on screen until the new one resolves, and the very
 *    first render shows a neutral skeleton box — raw mermaid source appears
 *    ONLY in the failed state, where it is genuinely the best we have.
 *  - CLICKABLE (round 4): given `parts`, the named nodes and edges become real
 *    buttons on the drawing. That happens AFTER the SVG lands, on the DOM
 *    mermaid produced (partHotspots.ts owns the matching), and it is undone on
 *    every re-render. With no parts, nothing about this component changes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useUiStore, type Theme } from '../../stores/uiStore';
import { decorateDiagramParts, markSelectedPart } from '../diagram/partHotspots';
import type { DiagramPart } from './transcript';

type MermaidApi = typeof import('mermaid').default;
type MermaidConfig = Parameters<MermaidApi['initialize']>[0];

let mermaidPromise: Promise<MermaidApi> | null = null;
let renderSeq = 0;

function loadMermaid(): Promise<MermaidApi> {
  if (mermaidPromise === null) {
    mermaidPromise = import('mermaid').then((m) => m.default);
  }
  return mermaidPromise;
}

/**
 * How long a fence that is still streaming in waits after its last delta
 * before it is drawn. Every delta changes the source; drawing each one is a
 * full mermaid pass thrown away by the next.
 */
export const STREAMING_RENDER_DELAY_MS = 300;

/**
 * A draw that fails is tried once more after this pause before the source is
 * shown. A live render can fail for reasons that pass on their own (the host
 * measured before it was laid out, mermaid's shared state mid-swap between
 * two drawings) while the same source draws on the next load (2026-09-09: a
 * board the reader saw as source rendered fine on reload); a real syntax
 * error fails twice, fast.
 */
export const RENDER_RETRY_DELAY_MS = 400;

/**
 * One config per theme and surface, built the first time each is drawn: the
 * tokens it reads only change with the theme, so re-reading computed styles
 * (and re-resolving every colour through the canvas) per diagram bought
 * nothing.
 */
const configCache = new Map<string, MermaidConfig>();

function mermaidConfig(theme: Theme, bare: boolean): MermaidConfig {
  const key = `${theme}:${bare ? 'canvas' : 'inset'}`;
  const cached = configCache.get(key);
  if (cached !== undefined) return cached;
  const config: MermaidConfig = {
    startOnLoad: false,
    theme: 'base',
    fontFamily: getComputedStyle(document.documentElement).getPropertyValue('--font-ui').trim(),
    flowchart: { ...MERMAID_FLOWCHART_CONFIG },
    // The pane floats on the canvas; a fence in prose sits in an inset
    // card. The edge-label chip has to match whichever it is.
    themeVariables: mermaidThemeVariables(bare ? '--bg-canvas' : '--bg-inset'),
    themeCSS: mermaidLabelStyles(),
  };
  configCache.set(key, config);
  return config;
}

/**
 * Renders run one at a time. `mermaid.initialize` is global, and two
 * diagrams on different surfaces (the pane, a fence in prose) rendering at
 * once used to overwrite each other's config before their own render ran:
 * each render here is preceded by its own initialize, with nothing between.
 */
let renderQueue: Promise<unknown> = Promise.resolve();

function renderInTurn(
  mermaid: MermaidApi,
  config: MermaidConfig,
  id: string,
  code: string,
  skip: () => boolean,
): Promise<{ svg: string } | null> {
  const run = renderQueue.then(() => {
    if (skip()) return null;
    mermaid.initialize(config);
    return mermaid.render(id, code);
  });
  renderQueue = run.catch(() => undefined);
  return run;
}

/**
 * Normalize any CSS color to a form mermaid's color library (khroma)
 * parses. The design tokens are oklch(); khroma only knows hex/rgb and
 * throws "Unsupported color format" — which silently downgraded EVERY
 * diagram to the code-block fallback. Painting 1×1 on a shared canvas and
 * reading the pixel back resolves whatever the browser can paint to plain
 * RGB bytes (the fillStyle getter itself PRESERVES oklch, so serialization
 * is not enough). Environments without canvas (jsdom) pass through.
 */
let colorCtx: CanvasRenderingContext2D | null | undefined;
export function cssColorToHex(value: string): string {
  if (colorCtx === undefined) {
    colorCtx = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (colorCtx !== null) {
      colorCtx.canvas.width = 1;
      colorCtx.canvas.height = 1;
    }
  }
  if (colorCtx === null) return value;
  try {
    colorCtx.clearRect(0, 0, 1, 1);
    colorCtx.fillStyle = value;
    colorCtx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = colorCtx.getImageData(0, 0, 1, 1).data;
    if (a === undefined || r === undefined || g === undefined || b === undefined) return value;
    if (a === 0) return value; // unpaintable input — leave it alone
    const hex = (n: number): string => n.toString(16).padStart(2, '0');
    return a === 255
      ? `#${hex(r)}${hex(g)}${hex(b)}`
      : `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
  } catch {
    return value;
  }
}

/**
 * Live token values → mermaid themeVariables (re-read per render).
 *
 * `behind` is the background the drawing SITS ON, which is not the same in
 * both places a diagram appears: the pane floats on the canvas, a fence in
 * prose sits inside an inset card. It matters because it is also the colour
 * of an edge label's backing chip — the little mask that stops the edge line
 * running through the words — and a chip in the wrong grey reads as a box
 * around the label instead of disappearing under it.
 */
export function mermaidThemeVariables(behind = '--bg-canvas'): Record<string, string> {
  const styles = getComputedStyle(document.documentElement);
  const token = (name: string, fallback: string): string => {
    const v = styles.getPropertyValue(name).trim();
    return cssColorToHex(v.length > 0 ? v : fallback);
  };
  const surface = token('--bg-raised', '#343746');
  const ink = token('--text-primary', '#f8f8f2');
  const line = token('--border-strong', '#6c6f85');
  const accent = token('--accent', '#bd93f9');
  return {
    // Node text is UI type, not prose type: the diagram is chrome the reader
    // scans, and 16px boxes crowd a picture that has to hold ten of them.
    fontSize: styles.getPropertyValue('--text-ui-size').trim() || '13px',
    background: token('--bg-inset', '#21222c'),
    primaryColor: surface,
    primaryTextColor: ink,
    primaryBorderColor: line,
    secondaryColor: token('--bg-surface', '#2b2d3a'),
    secondaryTextColor: ink,
    secondaryBorderColor: token('--border-default', '#44475a'),
    tertiaryColor: token('--bg-inset', '#21222c'),
    tertiaryTextColor: token('--text-secondary', '#b8bdd9'),
    tertiaryBorderColor: token('--border-default', '#44475a'),
    lineColor: line,
    textColor: ink,
    noteBkgColor: token('--accent-subtle', '#44475a'),
    noteTextColor: ink,
    noteBorderColor: accent,
    actorBorder: line,
    actorBkg: surface,
    actorTextColor: ink,
    labelBoxBkgColor: surface,
    labelTextColor: ink,
    clusterBkg: token('--bg-surface', '#2b2d3a'),
    clusterBorder: token('--border-default', '#44475a'),
    edgeLabelBackground: token(behind, '#282a36'),
    mainBkg: surface,
    nodeBorder: line,
    titleColor: ink,
  };
}

/**
 * How the classic shapes are DRESSED (round 5, owner: "Draft profile can be
 * better formatted… I just want super pretty looking diagrams that's easy to
 * understand").
 *
 * Three jobs, all of them presentation, all of them app-side — the model emits
 * shapes and words and never a colour:
 *
 *  - A LABEL CANNOT RUN AWAY. `wrappingWidth` is the hard cap on how wide any
 *    label grows before it wraps, and `overflow-wrap` breaks a token that has
 *    no space in it at all (a module path, a queue name) instead of letting it
 *    push the box out to its own length. One schema-dump node then costs three
 *    lines instead of the width of the pane.
 *  - AN EDGE LABEL READS AS A LABEL. Smaller than node text, regular weight
 *    against the node's medium, in the secondary ink rather than the primary —
 *    and sitting on an opaque chip in the pane's own background colour, so the
 *    edge line passes behind the words instead of through them. Mermaid's
 *    default chip is 50% transparent, which is exactly the collision.
 *  - THE SHAPES CARRY THE SAME WEIGHT. Service, worker, database, queue,
 *    external, decision, trigger are seven different outlines; one stroke
 *    weight across all of them (mermaid draws some as `rect`, some as
 *    `polygon`, some as `path`) is what makes them read as one vocabulary
 *    rather than seven borrowed styles.
 *
 * Every value is a token read live, so both themes are covered by one rule and
 * a token change lands here without anyone remembering to come back.
 */
export function mermaidLabelStyles(): string {
  const styles = getComputedStyle(document.documentElement);
  const size = (name: string, fallback: string): string =>
    styles.getPropertyValue(name).trim() || fallback;
  const quiet = cssColorToHex(size('--text-secondary', '#b8bdd9'));
  return [
    `.nodeLabel,.nodeLabel p{font-size:${size('--text-ui-size', '13px')};` +
      `line-height:${size('--text-ui-line', '20px')};font-weight:500;overflow-wrap:break-word;}`,
    `.edgeLabel,.edgeLabel p,.edgeLabel span{font-size:${size('--text-micro-size', '11px')};` +
      `line-height:${size('--text-micro-line', '16px')};font-weight:400;}`,
    `.edgeLabel span,.edgeLabel p{color:${quiet};fill:${quiet};}`,
    `.edgeLabel p{padding:0 4px;}`,
    `.node rect,.node polygon,.node circle,.node ellipse,.node path{stroke-width:1.25px;}`,
    `.flowchart-link,.edgePath .path{stroke-width:1.25px;}`,
  ].join('');
}

/**
 * The layout half of the same job. `wrappingWidth` caps BOTH node and edge
 * labels (mermaid writes it as an inline max-width on the label box), and the
 * spacings buy the room edge labels need so they land on their own line rather
 * than on top of a node or of each other.
 */
export const MERMAID_FLOWCHART_CONFIG = {
  htmlLabels: true,
  /** the cap on label width, in px, before it wraps */
  wrappingWidth: 200,
  padding: 10,
  nodeSpacing: 44,
  rankSpacing: 56,
  diagramPadding: 8,
} as const;

export function MermaidDiagram({
  code,
  onState,
  bare = false,
  streaming = false,
  parts,
  selectedPart = null,
  onSelectPart,
  onPartsDecorated,
}: {
  code: string;
  /** Told 'rendered' or 'failed' as each parse settles (the pane explains a
   *  failure; prose fences just show the source). */
  onState?: (state: 'rendered' | 'failed') => void;
  /** The fence is still streaming in: draw after a pause, not per delta. */
  streaming?: boolean;
  /** Pane presentation: no card chrome, and the drawing keeps its natural
   *  size so the pane's fit-to-view transform can frame it (DiagramView). */
  bare?: boolean;
  /** The clickable pieces the server named. Absent/empty = an inert picture. */
  parts?: readonly DiagramPart[];
  /** Label of the part whose panel is open (drawn as the current one). */
  selectedPart?: string | null;
  /** Told the label the reader clicked or pressed Enter/Space on. */
  onSelectPart?: (label: string) => void;
  /** Told which parts had nowhere to land in this drawing (see partHotspots). */
  onPartsDecorated?: (unmatched: readonly DiagramPart[]) => void;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const theme = useUiStore((s) => s.theme);
  const idRef = useRef<number | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  if (idRef.current === null) idRef.current = ++renderSeq;

  // Reporting channels, not inputs: a fresh callback identity must not
  // re-decorate (or worse, re-run mermaid) for nothing.
  const selectRef = useRef(onSelectPart);
  const decoratedRef = useRef(onPartsDecorated);
  useEffect(() => {
    selectRef.current = onSelectPart;
    decoratedRef.current = onPartsDecorated;
  });

  /**
   * The innerHTML payload, memoized on the SVG string. React re-applies
   * `dangerouslySetInnerHTML` when the PROP OBJECT changes identity, not when
   * the html inside it does, so a fresh object literal per render re-parses
   * the markup and silently throws away everything the hotspot decoration put
   * on it (which is how a click that opened a panel un-clicked every other
   * node). One stable object per drawing, and the DOM outlives re-renders.
   */
  const html = useMemo(() => (svg === null ? null : { __html: svg }), [svg]);

  /** The theme, surface and source last drawn: a flag flipping on its own
   *  (a fence closing) never draws the same picture twice. */
  const drawnRef = useRef<string | null>(null);

  useEffect(() => {
    const drawing = `${theme} ${bare ? 'canvas' : 'inset'} ${code}`;
    if (drawnRef.current === drawing) return;
    let cancelled = false;
    let timer: number | null = null;
    // Deliberately NOT clearing svg/failed here: while a re-render is in
    // flight (a theme toggle, a streaming fence growing) the previous
    // outcome stays on screen — blanking to raw mermaid source between two
    // good drawings read as flicker. The new outcome replaces it when it
    // settles; a render this effect superseded reports nothing (cancelled).
    const draw = (attempt = 1) => {
      loadMermaid()
        .then((mermaid) =>
          // Unique id per render pass — mermaid keeps internal element state.
          renderInTurn(
            mermaid,
            mermaidConfig(theme, bare),
            `lm-mermaid-${idRef.current}-${++renderSeq}`,
            code,
            () => cancelled,
          ),
        )
        .then((result) => {
          if (cancelled || result === null) return;
          drawnRef.current = drawing;
          setSvg(result.svg);
          setFailed(false);
          onState?.('rendered');
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const reason = err instanceof Error ? err.message : String(err);
          if (attempt === 1) {
            console.warn('[loremap] diagram render failed, trying once more:', reason);
            timer = window.setTimeout(() => draw(2), RENDER_RETRY_DELAY_MS);
            return;
          }
          console.warn('[loremap] diagram render failed:', reason);
          drawnRef.current = drawing;
          setSvg(null);
          setFailed(true);
          onState?.('failed');
        });
    };
    // A fence still streaming in changes with every delta: it is drawn after
    // a short pause in them, or at once when the fence closes.
    if (streaming) timer = window.setTimeout(() => draw(), STREAMING_RENDER_DELAY_MS);
    else draw();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  // onState is a reporting channel, not an input: re-rendering on a new
  // callback identity would re-run mermaid for nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, theme, bare, streaming]);

  /**
   * In the pane, the drawing keeps its NATURAL size and DiagramView's
   * transform does the framing (see its module note). Mermaid ships every SVG
   * with `width="100%"` plus an inline `max-width` of its natural width, which
   * together pin it to the left of whatever box it is in and leave its height
   * to be derived from that width — the two facts behind "the diagram is not
   * centred and is cut off". Its own viewBox already says how big it is, so
   * that is what it is told to be. A fence in prose keeps mermaid's responsive
   * sizing: there it should shrink into a narrow column.
   */
  useEffect(() => {
    if (!bare || svg === null) return;
    const el = hostRef.current?.querySelector('svg') ?? null;
    if (el === null) return;
    const box = (el.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
    const width = box[2];
    const height = box[3];
    if (width === undefined || height === undefined) return;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;
    el.removeAttribute('width');
    el.removeAttribute('height');
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    el.style.maxWidth = 'none';
  }, [svg, bare]);

  // The hotspots live on mermaid's own DOM, so they are applied after each
  // SVG lands and removed before it is replaced.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null || svg === null) return;
    const result = decorateDiagramParts(host, parts ?? [], {
      onSelect: (label) => selectRef.current?.(label),
    });
    decoratedRef.current?.(result.unmatched);
    return result.cleanup;
  }, [svg, parts]);

  // Which one is open is a MARK on the already-decorated elements, never a
  // re-decoration: rebuilding them would strip tabindex off whichever part
  // has focus and drop the keyboard reader out of the drawing.
  useEffect(() => {
    if (svg === null) return;
    markSelectedPart(hostRef.current, selectedPart);
  }, [svg, parts, selectedPart]);

  if (svg !== null && html !== null) {
    return (
      <div
        ref={hostRef}
        data-testid="mermaid-diagram"
        data-state="rendered"
        className={
          bare
            ? 'lm-diagram-canvas w-max'
            : 'my-3 overflow-x-auto rounded-card border border-default bg-inset p-3 first:mt-0 last:mb-0'
        }
        // securityLevel stays mermaid's default (strict): its output is sanitized.
        dangerouslySetInnerHTML={html}
      />
    );
  }
  // Failed: the plain code block — exactly what a non-mermaid fence renders,
  // so a broken diagram (cheap models do emit bad syntax) degrades to
  // something a reader can still read, copy and paste somewhere that parses.
  if (failed) {
    return (
      <pre
        data-testid="mermaid-fallback"
        data-state="failed"
        className={`mono overflow-x-auto rounded-card border border-default bg-inset p-3 ${
          // In the pane the fit scales whatever it is given, so the source is
          // wrapped to a readable measure rather than left as one long line
          // that would be shrunk to nothing.
          bare ? 'max-w-[72ch] whitespace-pre-wrap' : 'my-3'
        }`}
      >
        {code}
      </pre>
    );
  }
  // First render, nothing settled yet: a quiet placeholder box. Raw mermaid
  // source is a wall of syntax nobody asked to read — it is shown only when
  // the render FAILED and the source is genuinely the best we have.
  return (
    <div
      data-testid="mermaid-loading"
      data-state="loading"
      aria-hidden
      className={bare ? 'lm-skeleton h-40 w-80' : 'lm-skeleton my-3 h-40 first:mt-0 last:mb-0'}
    />
  );
}
